// CreateLeadModal.js

import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Autocomplete, Button, CardContent, CardActions, Divider, Grid, IconButton, Modal, TextField } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import MainCard from 'ui-component/cards/MainCard';
import CloseIcon from '@mui/icons-material/Close';
import { Formik } from 'formik';
import * as Yup from 'yup';
import axiosServices from 'utils/axios';
import { toast } from 'react-toastify';

const validationSchema = Yup.object().shape({
    name: Yup.string().required('Lead name is required'),
    listingId: Yup.string().required('Listing is required'),
    complexId: Yup.string().required('Complex is required'),
    email: Yup.string().email('Invalid email').required('Email is required'),
    contact: Yup.string().required('Contact is required'),
    sentDate: Yup.date().required('Sent Date is required')
});

export default function CreateLeadModal({ open, setOpen, editItem, clearEdit, refresh }) {
    const [properties, setProperties] = useState([]);
    const [complexesList, setComplexesList] = useState([]);

    useEffect(() => {
        // fetch approved properties
        axiosServices
            .get('/property', {
                headers: { authorization: `Bearer ${localStorage.getItem('serviceToken')}` },
                params: { page: 1, limit: 10000 }
            })
            .then((res) => {
                const all = res.data.properties;
                setProperties(all.filter((p) => p.listingStatus === 'APPROVED' && p.listing_id));
            })
            .catch(() => toast.error('Error fetching properties'));

        // fetch complexes
        axiosServices
            .get('/complex', {
                headers: { authorization: `Bearer ${localStorage.getItem('serviceToken')}` },
                params: { page: 1, limit: 1000 }
            })
            .then((res) => {
                setComplexesList(res.data.complexes || []);
            })
            .catch(() => toast.error('Error fetching complexes'));
    }, []);

    const initialValues = {
        name: editItem?.name || '',
        listingId: editItem?.property?.id || '',
        complexId: editItem?.complex?.id || '',
        email: editItem?.email || '',
        contact: editItem?.contact || '',
        ref: editItem?.ref || '',
        count: editItem?.count ?? 0,
        hits: editItem?.hits ?? 0,
        floorPlan: editItem?.floorPlan || '',
        sentDate: editItem?.sentDate?.slice(0, 10) || new Date().toISOString().slice(0, 10)
    };

    const handleClose = () => {
        clearEdit();
        setOpen(false);
    };

    const handleSubmit = async (values, { setSubmitting }) => {
        const payload = {
            name: values.name,
            listingId: values.listingId,
            complexId: values.complexId,
            ref: values.ref,
            count: values.count,
            hits: values.hits,
            floorPlan: values.floorPlan,
            email: values.email,
            contact: values.contact,
            sentDate: values.sentDate
        };

        try {
            if (editItem) {
                await axiosServices.patch(`/leads/${editItem.id}`, payload, {
                    headers: { authorization: `Bearer ${localStorage.getItem('serviceToken')}` }
                });
                toast.success('Lead updated');
            } else {
                await axiosServices.post('/leads', payload, {
                    headers: { authorization: `Bearer ${localStorage.getItem('serviceToken')}` }
                });
                toast.success('Lead created');
            }
            refresh();
            handleClose();
        } catch (err) {
            toast.error(err?.response?.data?.message || 'Error saving lead');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal open={open} onClose={handleClose}>
            <MainCard
                sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: { xs: 350, sm: 500 }
                }}
                title={editItem ? 'Edit Lead' : 'Create Lead'}
                secondary={
                    <IconButton onClick={handleClose}>
                        <CloseIcon />
                    </IconButton>
                }
                content={false}
            >
                <Formik enableReinitialize initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
                    {({ values, errors, touched, handleChange, handleBlur, setFieldValue, isSubmitting, handleSubmit }) => (
                        <form noValidate onSubmit={handleSubmit}>
                            <CardContent>
                                <Grid container spacing={2}>
                                    {/* Listing selector */}
                                    <Grid item xs={12} md={6}>
                                        <Autocomplete
                                            options={properties}
                                            getOptionLabel={(o) => o.listing_id}
                                            isOptionEqualToValue={(o, v) => o.id === v.id}
                                            value={properties.find((p) => p.id === values.listingId) || null}
                                            onChange={(_, newVal) => {
                                                setFieldValue('listingId', newVal?.id || '');
                                                setFieldValue('floorPlan', newVal?.generalInformation?.floorPlanName || '');
                                            }}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label="Listing Id"
                                                    name="listingId"
                                                    onBlur={handleBlur}
                                                    error={touched.listingId && Boolean(errors.listingId)}
                                                    helperText={touched.listingId && errors.listingId}
                                                />
                                            )}
                                        />
                                    </Grid>

                                    {/* Complex selector */}
                                    <Grid item xs={12} md={6}>
                                        <Autocomplete
                                            options={complexesList}
                                            getOptionLabel={(c) => c.name || c.id}
                                            isOptionEqualToValue={(c, v) => c.id === v.id}
                                            value={complexesList.find((c) => c.id === values.complexId) || null}
                                            onChange={(_, newVal) => {
                                                setFieldValue('complexId', newVal?.id || '');
                                            }}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label="Complex"
                                                    name="complexId"
                                                    onBlur={handleBlur}
                                                    error={touched.complexId && Boolean(errors.complexId)}
                                                    helperText={touched.complexId && errors.complexId}
                                                />
                                            )}
                                        />
                                    </Grid>

                                    {/* Lead Name */}
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label="Agent Name"
                                            name="name"
                                            value={values.name}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={touched.name && Boolean(errors.name)}
                                            helperText={touched.name && errors.name}
                                        />
                                    </Grid>

                                    {/* Email, Contact */}
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Email"
                                            name="email"
                                            type="email"
                                            value={values.email}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={touched.email && Boolean(errors.email)}
                                            helperText={touched.email && errors.email}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Contact"
                                            name="contact"
                                            value={values.contact}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={touched.contact && Boolean(errors.contact)}
                                            helperText={touched.contact && errors.contact}
                                        />
                                    </Grid>

                                    {/* Ref, Count, Hits */}
                                    {['ref', 'count', 'hits'].map((fld) => (
                                        <Grid item xs={12} sm={fld === 'ref' ? 12 : 6} key={fld}>
                                            <TextField
                                                fullWidth
                                                label={fld.charAt(0).toUpperCase() + fld.slice(1)}
                                                name={fld}
                                                type={fld === 'ref' ? 'text' : 'number'}
                                                value={values[fld]}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                error={touched[fld] && Boolean(errors[fld])}
                                                helperText={touched[fld] && errors[fld]}
                                            />
                                        </Grid>
                                    ))}

                                    {/* Floor Plan (read-only) */}
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label="Floor Plan"
                                            name="floorPlan"
                                            value={values.floorPlan}
                                            InputProps={{ readOnly: true }}
                                        />
                                    </Grid>

                                    {/* Sent Date */}
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label="Sent Date"
                                            name="sentDate"
                                            type="date"
                                            InputLabelProps={{ shrink: true }}
                                            value={values.sentDate}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={touched.sentDate && Boolean(errors.sentDate)}
                                            helperText={touched.sentDate && errors.sentDate}
                                        />
                                    </Grid>
                                </Grid>
                            </CardContent>

                            <Divider />

                            <CardActions sx={{ justifyContent: 'flex-end' }}>
                                <Button variant="outlined" onClick={handleClose}>
                                    Cancel
                                </Button>
                                <LoadingButton type="submit" variant="contained" loading={isSubmitting}>
                                    {editItem ? 'Update' : 'Create'}
                                </LoadingButton>
                            </CardActions>
                        </form>
                    )}
                </Formik>
            </MainCard>
        </Modal>
    );
}

CreateLeadModal.propTypes = {
    open: PropTypes.bool.isRequired,
    setOpen: PropTypes.func.isRequired,
    editItem: PropTypes.object,
    clearEdit: PropTypes.func.isRequired,
    refresh: PropTypes.func.isRequired
};
